import type { ComponentType, ReactNode } from 'react';
import { Link, useRouter, useRouterState } from '@tanstack/react-router';

/**
 * Navegação para as telas da Base de Erros Conhecidos.
 *
 * Mesmo motivo de `features/tickets/navigation.tsx`: o `to` do TanStack Router
 * é tipado contra a árvore registrada em `app/router.tsx`, e este módulo é
 * compilado antes de a rota existir lá.
 */

export const CAMINHO_ERROS = '/erros-conhecidos';
export const CAMINHO_ERRO = '/erros-conhecidos/$erroId';

interface PropsDeLink {
  to: string;
  params?: Record<string, string>;
  className?: string;
  title?: string;
  children: ReactNode;
}

const LinkDeRota = Link as unknown as ComponentType<PropsDeLink>;

export function LinkDoErroConhecido({
  erroId,
  className,
  title,
  children,
}: {
  erroId: string;
  className?: string;
  title?: string;
  children: ReactNode;
}) {
  return (
    <LinkDeRota to={CAMINHO_ERRO} params={{ erroId }} className={className} title={title}>
      {children}
    </LinkDeRota>
  );
}

export function LinkDaBaseDeErros({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  return (
    <LinkDeRota to={CAMINHO_ERROS} className={className}>
      {children}
    </LinkDeRota>
  );
}

/**
 * O termo de busca vive na URL, não no estado da lista.
 *
 * Mesma decisão de `features/tickets/navigation.tsx`: com a URL como fonte
 * única, o resultado de uma pesquisa pode ser mandado por mensagem — que é
 * exatamente o que se faz com um erro conhecido durante um plantão.
 */
export function useBuscaDeErros(): { termo: string; definir: (termo: string) => void } {
  const router = useRouter();
  const busca = useRouterState({ select: (estado) => estado.location.search }) as { q?: unknown };

  const navegar = router.navigate as unknown as (destino: {
    to: string;
    search: Record<string, string>;
    replace: boolean;
  }) => Promise<void>;

  return {
    termo: typeof busca.q === 'string' ? busca.q : '',
    definir: (termo) => {
      // `replace` para a busca não encher o histórico: voltar precisa sair da
      // lista, e não desfazer letra por letra o que foi pesquisado.
      void navegar({ to: CAMINHO_ERROS, search: termo ? { q: termo } : {}, replace: true });
    },
  };
}
